import React from 'react';

/**
 * Pre-renders the array of options to check how many are visible
 * @param options
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useTabOverflow = (options: Array<any>) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [visibleCount, setVisibleCount] = React.useState(options.length);

  // Monitor display port changes
  React.useEffect(() => {
    if (!containerRef.current) return () => null;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  React.useLayoutEffect(() => {
    if (!containerRef.current) return;

    if (!containerWidth) {
      setContainerWidth(containerRef.current.clientWidth);
    }

    const tabNodes = Array.from(
      containerRef.current.querySelectorAll('[data-tab]'),
    ) as HTMLElement[];

    // The tabs render as flex items with a CSS `gap` between them (on their
    // immediate parent, not necessarily `containerRef` itself) — that space
    // is real and has to be budgeted too, or the last "visible" tab ends up
    // sitting slightly past the container's edge.
    const gap =
      parseFloat(
        (tabNodes[0]?.parentElement &&
          getComputedStyle(tabNodes[0].parentElement).columnGap) ||
          '0',
      ) || 0;

    // Whenever some tabs don't fit, a "more" button renders alongside the
    // visible ones — so its own width (plus the gap before it) has to come
    // out of the budget too, otherwise the last tab that "fits" pushes the
    // button (and everything after it) past the container's edge.
    const totalWidth = tabNodes.reduce(
      (sum, tab, i) => sum + tab.offsetWidth + (i > 0 ? gap : 0),
      0,
    );
    if (totalWidth <= containerWidth) {
      setVisibleCount(tabNodes.length);
      return;
    }

    const moreButtonNode = containerRef.current.querySelector(
      '[data-more-button]',
    ) as HTMLElement | null;
    const moreButtonWidth = moreButtonNode?.offsetWidth ?? 0;

    let used = 0;
    let count = 0;

    tabNodes.forEach((tab) => {
      const w = tab.offsetWidth;
      const gapBeforeTab = count > 0 ? gap : 0;
      // The more-button (and the gap before it) must still fit after this
      // tab — otherwise this tab can't be the last visible one.
      const gapBeforeMoreButton = gap;
      if (
        used + gapBeforeTab + w + gapBeforeMoreButton + moreButtonWidth <=
        containerWidth
      ) {
        used += gapBeforeTab + w;
        count += 1;
      }
    });

    setVisibleCount(count);
  }, [containerWidth, options]);

  return { containerRef, visibleCount };
};
